import { Box, Button, Grid, Typography } from "@mui/material";
import Header from "../../../components/Header/Header";
import './CreateQuizPage.css';
import SamplePDF from '../../../assets/files/sample.pdf';
import { useState } from "react";
import UploadFileModal from "../../../components/QuizComponents/Modals/UploadFileModal/UploadFileModal";
import ManualModal from "../../../components/QuizComponents/Modals/ManualModal/ManualModal";
import { useLocation, useNavigate } from "react-router-dom";

const navLinks = [
];
function CreateQuizPage() {

    const location = useLocation();
    const navigate = useNavigate();
    const classId = location.state?.classId || null;

    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isManualModalOpen, setIsManualModalOpen] = useState(false);

    const handleDownloadSample = () => {
        const link = document.createElement('a');
        link.href = SamplePDF;
        link.download = 'EDUWaves_Sample.pdf';
        link.click();
    };

    const handleAIGeneratedQuiz = () => {
        const queryParams = classId ? `?classId=${classId}` : '';
        navigate(`/ai-generate-quiz${queryParams}`);
    };

    return (
        <>
            <Header navLinks={navLinks} isScrollEffect={false} />
            <Box className="page-container">
                <Typography className="page-title" variant="h3" gutterBottom>
                    Create Your Assessment Using AI Tools On EDUWaves
                </Typography>
                <Typography className="page-subtitle" variant="h5" gutterBottom>
                    Your first step toward interactive learning
                </Typography>
                <Typography className="page-description" paragraph>
                    Experience EDUWaves' powerful features by creating your Assessments on our platform. <br /> 
                    Choose between three flexible options:
                    <ul>
                        <li>
                            <strong>Upload a PDF:</strong> We'll process your file to extract and organize your questions, allowing you to review them before proceeding.
                        </li>
                        <li>
                            <strong>Build from Scratch:</strong> Manually create your Assessment directly on the platform with our intuitive tools.
                        </li>
                        <li>
                            <strong>Generate using AI:</strong> Let our AI assistant create a complete quiz based on your requirements and specifications.
                        </li>
                    </ul>
                </Typography>

                <Typography className="important-note" paragraph>
                    <strong>For best results when uploading a PDF:</strong>
                    <ul>
                        <li>Use numbers like <code>1.</code>, <code>2.</code>, <code>3.</code> to organize your questions sequentially.</li>
                        <li>Ensure any images appear immediately below the associated question text.</li>
                        <li>If questions have choices, list them using letters like <code>A.</code>, <code>B.</code>, <code>C.</code>.</li>
                        <li>(Optional) Include the correct answer by using the format <strong>"Correct: Answer"</strong>.</li>
                        <li>For subquestions, use a format like <strong>"Subquestions for question x:"</strong> followed by <code>1-</code>, <code>2-</code>, <code>3-</code>.</li>
                    </ul>   
                </Typography>

                <Typography className="download-prompt" paragraph>
                    Need guidance? <span className="text-button" onClick={handleDownloadSample}> Download our <strong>sample PDF</strong> for a detailed example. </span> 
                </Typography>

                <Grid container spacing={2} className="actions-container">
                    <Grid item>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setIsUploadModalOpen(true)}
                            sx={{
                                backgroundColor: 'var(--primary-purple)',
                                color: 'var(--light-gray)',
                                fontFamily: 'var(--font-title)',
                                borderRadius: '8px',
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                textTransform: 'capitalize',
                                padding: { xs: '8px 16px', md: '12px 24px' },
                                minWidth: { xs: 120, md: 160 },
                                fontWeight: 600,
                                boxShadow: 2,
                                border: 'none',
                                cursor: 'pointer',
                                outline: 'none',
                                transition: 'background 0.2s',
                                '&:hover': {
                                    backgroundColor: 'var(--dark-purple)',
                                },
                            }}
                        >
                            Upload PDF
                        </Box>
                    </Grid>
                    <Grid item>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setIsManualModalOpen(true)}
                            sx={{
                                backgroundColor: 'var(--dark-purple)',
                                color: 'var(--light-gray)',
                                fontFamily: 'var(--font-title)',
                                borderRadius: '8px',
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                textTransform: 'capitalize',
                                padding: { xs: '8px 16px', md: '12px 24px' },
                                minWidth: { xs: 120, md: 160 },
                                fontWeight: 600,
                                border: '2px solid var(--dark-purple)',
                                boxShadow: 2,
                                cursor: 'pointer',
                                outline: 'none',
                                transition: 'background 0.2s, border-color 0.2s',
                                '&:hover': {
                                    backgroundColor: 'var(--primary-purple)',
                                    borderColor: 'var(--primary-purple)',
                                },
                            }}
                        >
                            Build Manually
                        </Box>
                    </Grid>
                    <Grid item>
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'inline-block',
                            }}
                        >
                            <Box
                                component="button"
                                type="button"
                                onClick={handleAIGeneratedQuiz}
                                sx={{
                                    backgroundColor: 'white',
                                    color: 'var(--deep-purple)',
                                    border: '2px solid var(--deep-purple)',
                                    borderRadius: '8px',
                                    fontSize: { xs: '1rem', md: '1.1rem' },
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    padding: { xs: '8px 16px', md: '12px 24px' },
                                    minWidth: { xs: 120, md: 180 },
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    position: 'relative',
                                    zIndex: 1,
                                    '&:hover': {
                                        backgroundColor: 'var(--deep-purple)',
                                        borderColor: 'var(--deep-purple)',
                                        color: 'white',
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                Generate using AI
                            </Box>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    zIndex: 2,
                                    background: '#e4c646',
                                    borderRadius: '50%',
                                    width: 26,
                                    height: 26,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: 1,
                                    border: '1.5px solid #e4c646',
                                    p: 0,
                                }}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 20 20"
                                    fill="white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ display: 'block' }}
                                >
                                    <path d="M10 2l2.39 4.84 5.34.78-3.87 3.77.91 5.32L10 14.27l-4.77 2.51.91-5.32L2.27 7.62l5.34-.78L10 2z"/>
                                </svg>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <UploadFileModal 
                    open={isUploadModalOpen} 
                    onClose={() => setIsUploadModalOpen(false)} 
                    classId={classId}
                />
                <ManualModal 
                    open={isManualModalOpen} 
                    onClose={() => setIsManualModalOpen(false)} 
                    classId={classId}
                />

            </Box>
        </>
    );
}
export default CreateQuizPage;
